/**
 * Browse a connected relational database. Rendered inside
 * `ConnectionBrowserPage`, which owns the page header and the connection
 * picker (ADR-0022).
 *
 * Schema → table → its columns and a preview of its rows. Three of those four
 * steps are metadata and emit no audit; the fourth — the rows — is an access
 * event and is audited server-side, which is why the preview is a button
 * rather than something that happens as you click around.
 *
 * **Read-only.** There is nothing on this page that writes, because there is
 * no endpoint that writes.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconDatabase, IconEye, IconLock } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError } from "@/shared/ui/toast";
import {
  databaseColumns,
  databaseRead,
  databaseSchemas,
  databaseTables,
  type DatabaseRows,
  type SchemaSummary,
  type TableSchema,
  type TableSummary,
} from "@/shared/api/database";

/** One cell, as text. `null` is shown as a dash so an empty string and a NULL
 *  do not look the same — the difference matters in a source system. */
function cellText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * A result table. Exported because the Database Source node's Test preview
 * shows the same thing, and two tables that drift apart would make the preview
 * a different answer from the browser's.
 */
export function DatabaseRowsTable({
  rows,
  columns,
  max = 50,
}: {
  rows: Record<string, unknown>[];
  columns: string[];
  max?: number;
}) {
  const shown = rows.slice(0, max);
  if (columns.length === 0) return <Text size="xs">No columns.</Text>;
  return (
    <div style={{ overflowX: "auto" }}>
      <Text size="xs" c="dimmed" mb={4}>
        {rows.length.toLocaleString()} {rows.length === 1 ? "row" : "rows"} × {columns.length}{" "}
        {columns.length === 1 ? "column" : "columns"}
        {rows.length > shown.length ? ` · showing first ${shown.length}` : ""}
      </Text>
      <Table striped withTableBorder verticalSpacing={4} fz="xs">
        <Table.Thead>
          <Table.Tr>
            {columns.map((c) => (
              <Table.Th key={c}>{c}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.map((row, i) => (
            <Table.Tr key={i}>
              {columns.map((c) => (
                <Table.Td key={c}>{cellText(row[c])}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

export function DatabaseBrowser({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();
  const [schemas, setSchemas] = useState<SchemaSummary[]>([]);
  const [schema, setSchema] = useState<string | null>(null);
  const [tables, setTables] = useState<TableSummary[]>([]);
  const [table, setTable] = useState<string | null>(null);
  const [described, setDescribed] = useState<TableSchema | null>(null);
  const [preview, setPreview] = useState<DatabaseRows | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const token = await getToken();
        const result = await databaseSchemas(token, connectionId);
        if (cancelled) return;
        setSchemas(result.items);
        // One schema is not a choice — make it, the way the connection picker
        // does with a single connection.
        if (result.items.length === 1 && result.items[0]) setSchema(result.items[0].name);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!schema) {
      setTables([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const result = await databaseTables(token, connectionId, schema);
        if (!cancelled) setTables(result.items);
      } catch (err) {
        if (!cancelled) toastApiError("Could not list tables", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, schema]);

  useEffect(() => {
    if (!schema || !table) {
      setDescribed(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const result = await databaseColumns(token, connectionId, schema, table);
        if (!cancelled) setDescribed(result);
      } catch (err) {
        if (!cancelled) toastApiError("Could not describe the table", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, schema, table]);

  const onPreview = useCallback(async () => {
    if (!schema || !table) return;
    setPreviewing(true);
    try {
      const token = await getToken();
      setPreview(await databaseRead(token, connectionId, { schema, table, limit: 100 }));
    } catch (err) {
      toastApiError("Could not read rows", err);
    } finally {
      setPreviewing(false);
    }
  }, [getToken, connectionId, schema, table]);

  const chosen = tables.find((t) => t.name === table) ?? null;

  return (
    <Stack gap="md">
      <Alert variant="light" icon={<IconLock size={16} />}>
        <Text size="sm">
          This connection is read-only: the platform can list and read, and has no code path that
          writes to a source database. Reading rows is recorded in the audit trail; listing schemas
          and tables is not.
        </Text>
      </Alert>

      {error && (
        <Alert color="red" variant="light" title="Could not reach the database">
          <Text size="sm">{error}</Text>
        </Alert>
      )}

      {loading && <Loader size="sm" />}

      {!loading && !error && schemas.length === 0 && (
        <Alert variant="light" icon={<IconDatabase size={16} />}>
          <Text size="sm">
            No schemas are visible to this account. A read-only account still needs USAGE on the
            schemas it should see.
          </Text>
        </Alert>
      )}

      <Group align="flex-end" gap="sm">
        <Select
          label="Schema"
          placeholder="Pick a schema"
          data={schemas.map((s) => ({
            value: s.name,
            label: `${s.name} (${s.table_count})`,
          }))}
          value={schema}
          onChange={(value) => {
            setSchema(value);
            setTable(null);
            setPreview(null);
          }}
          searchable={schemas.length > 5}
          w={280}
        />
        <Select
          label="Table or view"
          placeholder={schema ? "Pick a table" : "Pick a schema first"}
          data={tables.map((t) => ({
            value: t.name,
            label: t.kind === "view" ? `${t.name} (view)` : t.name,
          }))}
          value={table}
          onChange={(value) => {
            setTable(value);
            setPreview(null);
          }}
          disabled={!schema}
          searchable={tables.length > 5}
          w={320}
        />
        <Button
          variant="light"
          leftSection={<IconEye size={16} />}
          onClick={() => void onPreview()}
          loading={previewing}
          disabled={!schema || !table}
        >
          Preview rows
        </Button>
      </Group>

      {chosen?.row_estimate != null && (
        <Text size="xs" c="dimmed">
          About {chosen.row_estimate.toLocaleString()} rows (the planner&apos;s estimate, not a
          count).
        </Text>
      )}

      {described && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="xs">
            <Group gap="xs">
              <Text fw={600} size="sm">
                Columns
              </Text>
              {described.primary_key.length > 0 && (
                <Badge size="sm" variant="light">
                  key: {described.primary_key.join(", ")}
                </Badge>
              )}
            </Group>
            <Table striped withTableBorder verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Column</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Nullable</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {described.columns.map((c) => (
                  <Table.Tr key={c.name}>
                    <Table.Td>
                      {c.name}
                      {c.primary_key && (
                        <Badge ml={6} size="xs" variant="light">
                          PK
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{c.type_name}</Table.Td>
                    <Table.Td>{c.nullable ? "yes" : "no"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {described.foreign_keys.length > 0 && (
              <Text size="xs" c="dimmed">
                References:{" "}
                {described.foreign_keys
                  .map(
                    (fk) =>
                      `${fk.columns.join(", ")} → ${fk.referred_table ?? "?"}(${fk.referred_columns.join(", ")})`,
                  )
                  .join(" · ")}
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      {preview && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="xs">
            {preview.truncated && (
              <Text size="xs" c="yellow.7">
                Large table — showing the first {preview.max_rows} rows.
              </Text>
            )}
            <DatabaseRowsTable rows={preview.rows} columns={preview.columns} />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
